import { useToast } from '@chakra-ui/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { finleyAssert, date as commonDate } from '@finley-technologies/common';
import { Loan, typeguards } from '@finley-technologies/finley-types';

import {
  EditFundingRequestChangesetGQLType,
  EditFundingComponentChangesetGQLType,
  useGetResolvedLoanByIdQuery,
  useSubmitLoanEditRequestMutation,
  useGetActiveEditLoanEntitiesForLoanQuery,
  ResolvedLoanFragmentGQLType,
  FundingRequestEntitiesFragmentGQLType,
  ShareFundingComponentFragmentGQLType,
  LoanActionRecordFragmentGQLType,
  LoanEditDestinationDataFragmentGQLType,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import * as types from './types';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { successToastConfig } from '~/packages/common-frontend/constants';

const RouteParamsStruct = z.object({
  loanId: z.string({ message: 'Missing URL parameter' }),
});

type EditContextValue = {
  originalResolvedLoan: ResolvedLoanFragmentGQLType;
  newEntities: {
    loanAction: LoanActionRecordFragmentGQLType;
    newResolvedLoan: ResolvedLoanFragmentGQLType;
  } | null;
};

export type EditLoanContextReady = {
  status: 'ready';
  state: EditContextValue;
};

export type EditLoanContextType =
  | {
      status: 'mounting';
      state: null;
    }
  | {
      status: 'loading';
      state: null;
    }
  | EditLoanContextReady;

const mountingContext: EditLoanContextType = {
  status: 'mounting',
  state: null,
};

const loadingContext: EditLoanContextType = {
  status: 'loading',
  state: null,
};

const _createFundingRequestComponentsFormState = (params: {
  fundingRequestComponents: ShareFundingComponentFragmentGQLType[];
  componentChangesets: Loan.EditFundingComponentChangesetMetadata[] | null;
}): types.FundingRequestComponentsState => {
  const { fundingRequestComponents, componentChangesets } = params;
  return fundingRequestComponents.reduce<types.FundingRequestComponentsState>(
    (acc, frc) => {
      let fundTransferDate = frc.fundTransferDate ?? null;
      if (componentChangesets) {
        const targetChangeset = componentChangesets.find(
          (x) =>
            x.newFundingRequestComponentId === frc.fundingRequestComponentId,
        );
        finleyAssert(
          targetChangeset,
          `Failed to find funding component changeset while generating form fields [fundingRequestComponentId:${frc.fundingRequestComponentId}]`,
        );
        fundTransferDate = targetChangeset.fundTransferDate;
      }

      // eslint-disable-next-line no-param-reassign
      acc[frc.fundingRequestComponentId] = {
        id: frc.fundingRequestComponentId,
        amount: frc.requestedAmount,
        lenderShareId: frc.lenderShareId,
        capitalProviderId: frc.lenderOrganizationId,
        type: frc.type,
        fundTransferDate,
      };

      return acc;
    },
    {},
  );
};

const _createFundingRequestFormState = (params: {
  fundingRequests: FundingRequestEntitiesFragmentGQLType[];
  fundingRequestChangesets: Loan.EditFundingRequestChangesetMetadata[] | null;
}): types.FundingRequestsFormState => {
  const { fundingRequests, fundingRequestChangesets } = params;
  return fundingRequests.reduce<types.FundingRequestsFormState>((acc, fr) => {
    let componentChangesets:
      | Loan.EditFundingComponentChangesetMetadata[]
      | null = null;
    if (fundingRequestChangesets) {
      const fundingChangeSet = fundingRequestChangesets.find(
        (x) => x.newFundingRequestId === fr.fundingRequestId,
      );
      finleyAssert(
        fundingChangeSet,
        `Failed to find funding request changeset while generating form fields [fundingRequestId:${fr.fundingRequestId}]`,
      );
      componentChangesets = fundingChangeSet.fundingRequestComponents;
    }

    // eslint-disable-next-line no-param-reassign
    acc[fr.fundingRequestId] = {
      id: fr.fundingRequestId,
      totalAmount: fr.totalRequestedAmount,
      type: fr.fundingRequestType,
      targetFundingDate: fr.fundingComponents[0].targetFundingDate, // needs to change if support funding request component
      components: _createFundingRequestComponentsFormState({
        fundingRequestComponents: fr.fundingComponents,
        componentChangesets,
      }),
    };
    return acc;
  }, {});
};

const createInitialFormFields = (params: {
  originalResolvedLoan: ResolvedLoanFragmentGQLType;
  newResolvedLoan: ResolvedLoanFragmentGQLType | null;
  editLoanEntities: LoanEditDestinationDataFragmentGQLType | null;
}): types.EditLoanFormFields => {
  const { editLoanEntities, newResolvedLoan, originalResolvedLoan } = params;

  if (!newResolvedLoan || !editLoanEntities) {
    return {
      externalLoanId: originalResolvedLoan.externalLoanId ?? undefined,
      maturityDate: originalResolvedLoan.dates.maturityDate,
      pricingOptionKey: originalResolvedLoan.pricing.key,
      fundingRequests: _createFundingRequestFormState({
        fundingRequests: originalResolvedLoan.funding.requests,
        fundingRequestChangesets: null,
      }),
    };
  }

  finleyAssert(
    typeguards.loan.isEditSubmittedLoanActionMetadata(
      editLoanEntities.loanAction.metadata,
    ),
    `Detected invalid loan action edit metadata structure while generating form fields`,
  );

  const { fundingRequestChangesets } = editLoanEntities.loanAction.metadata;
  return {
    externalLoanId: newResolvedLoan.externalLoanId ?? undefined,
    maturityDate: newResolvedLoan.dates.maturityDate,
    pricingOptionKey: newResolvedLoan.pricing.key,
    fundingRequests: _createFundingRequestFormState({
      fundingRequests: newResolvedLoan.funding.requests,
      fundingRequestChangesets,
    }),
  };
};

const EditLoanContext = createContext<EditLoanContextType>(mountingContext);

export const useEditLoanContext = () => useContext(EditLoanContext);

export const EditLoanProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const params = useParams();
  const { loanId: originalLoanId } = RouteParamsStruct.parse(params);

  const { user } = useUserAuthOrThrow();
  const toast = useToast();
  const navigate = useNavigate();

  // ***** STATES *****
  const [state, setState] = useState<EditLoanContextType>(mountingContext);
  const formMethods = useForm<types.EditLoanFormFields>({
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  // ***** DATA & APIs *****
  const originalResolvedLoanResult = useGetResolvedLoanByIdQuery({
    variables: { loanId: originalLoanId },
    fetchPolicy: 'no-cache',
  });
  if (originalResolvedLoanResult.error) throw originalResolvedLoanResult.error;

  const editLoanEntities = useGetActiveEditLoanEntitiesForLoanQuery({
    fetchPolicy: 'network-only',
    variables: { loanId: originalLoanId },
  });
  if (editLoanEntities.error) throw editLoanEntities.error;

  const newResolvedLoanResult = useGetResolvedLoanByIdQuery({
    fetchPolicy: 'network-only',
    skip: !editLoanEntities.data?.loanEditData.destination,
    variables: {
      loanId: editLoanEntities.data?.loanEditData.destination?.loanId ?? '',
    },
  });
  if (newResolvedLoanResult.error) throw newResolvedLoanResult.error;

  const [
    submitLoanEditRequest,
    submitLoanRequestResults,
  ] = useSubmitLoanEditRequestMutation();
  if (submitLoanRequestResults.error) throw submitLoanRequestResults.error;

  // ***** SUBMIT *****
  const onSubmit = useCallback(
    async (formFields: types.EditLoanFormFields): Promise<void> => {
      if (!originalResolvedLoanResult.data) return;

      const today = commonDate.currentDateStr();

      const fundingRequestValues = Object.values(formFields.fundingRequests);
      const fundingRequestChangesets = fundingRequestValues.map<EditFundingRequestChangesetGQLType>(
        (fundingRequest) => {
          const { components, targetFundingDate } = fundingRequest;

          // We'll derive "postingDate" based on the "targetFundingDate".
          // If "targetFundingDate" is prior to today's date (it's in the past),
          // then we'll treat that as the intended "posting" (settlement) date
          const postingDate =
            targetFundingDate < today ? targetFundingDate : null;

          const componentValues = Object.values(components);
          const fundingRequestComponents = componentValues.map<EditFundingComponentChangesetGQLType>(
            (component) => {
              return {
                originalFundingRequestComponentId: component.id,
                type: component.type,
                amount: component.amount,
                capitalProviderId: component.capitalProviderId,
                fundTransferDate: component.fundTransferDate,
                // Currently using the same "posting" (settled) and "target funding"
                // date across all components based on parent funding request.
                // Can readjust this if need to support distinct dates per component.
                targetFundingDate,
                postingDate,
              };
            },
          );

          return {
            originalFundingRequestId: fundingRequest.id,
            fundingRequestComponents,
          };
        },
      );

      await submitLoanEditRequest({
        variables: {
          creditFacilityId:
            originalResolvedLoanResult.data.loan.facility.creditFacilityId,
          originalLoanId,
          proposedUserId: user.id,
          loanAttributes: {
            externalLoanId: formFields.externalLoanId,
            maturityDate: formFields.maturityDate,
          },
          fundingRequestChangesets,
        },
      });

      toast({
        ...successToastConfig,
        duration: 3000,
        title: 'Successfully submitted loan edit request',
      });

      navigate(`/loans/${originalLoanId}`);
    },
    [
      originalLoanId,
      navigate,
      originalResolvedLoanResult.data,
      submitLoanEditRequest,
      toast,
      user.id,
    ],
  );

  // ***** CONTEXT STATE *****
  useEffect(() => {
    if (
      originalResolvedLoanResult.loading ||
      editLoanEntities.loading ||
      newResolvedLoanResult.loading ||
      !originalResolvedLoanResult.data
    ) {
      setState(loadingContext);
      return;
    }

    const formFields = createInitialFormFields({
      originalResolvedLoan: originalResolvedLoanResult.data.loan,
      newResolvedLoan: newResolvedLoanResult.data?.loan ?? null,
      editLoanEntities: editLoanEntities.data?.loanEditData.destination ?? null,
    });
    formMethods.reset(formFields);

    setState({
      status: 'ready',
      state: {
        originalResolvedLoan: originalResolvedLoanResult.data.loan,
        newEntities:
          editLoanEntities.data?.loanEditData.destination &&
          newResolvedLoanResult.data
            ? {
                loanAction:
                  editLoanEntities.data.loanEditData.destination.loanAction,
                newResolvedLoan: newResolvedLoanResult.data.loan,
              }
            : null,
      },
    });
  }, [
    formMethods,
    originalResolvedLoanResult.loading,
    originalResolvedLoanResult.data,
    editLoanEntities.loading,
    editLoanEntities.data?.loanEditData.destination,
    newResolvedLoanResult.loading,
    newResolvedLoanResult.data,
  ]);

  return (
    <EditLoanContext.Provider value={state}>
      <FormProvider {...formMethods}>
        <form id="edit-loan" onSubmit={formMethods.handleSubmit(onSubmit)}>
          {children}
        </form>
      </FormProvider>
    </EditLoanContext.Provider>
  );
};
